// Canvas helpers: turn the loaded image (whole, or a drawn region) into a canvas
// that OCR reads well. We upscale small crops a little because Tesseract does
// better with larger glyphs.

import type { Region } from "../models/types.js";
import { MAX_LONG_EDGE, MIN_LONG_EDGE } from "../constants.js";

/** Scale factor for a scan: upscale tiny crops so glyphs are chunky enough,
 *  downscale huge phone photos so dot-matrix print fuses into solid strokes. */
export function computeScanScale(width: number, height: number): number {
  const longEdge = Math.max(width, height);
  if (longEdge < MIN_LONG_EDGE) return MIN_LONG_EDGE / longEdge;
  if (longEdge > MAX_LONG_EDGE) return MAX_LONG_EDGE / longEdge;
  return 1;
}

/** Draw the whole image (optionally just a region) onto a canvas, rescaled so
 *  the text is at a size OCR reads well. */
export function makeScanCanvas(
  img: HTMLImageElement,
  region?: Region | null,
): HTMLCanvasElement {
  const srcX = region ? region.x : 0;
  const srcY = region ? region.y : 0;
  const srcW = region ? region.w : img.naturalWidth;
  const srcH = region ? region.h : img.naturalHeight;

  const scale = computeScanScale(srcW, srcH);

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(srcW * scale);
  canvas.height = Math.round(srcH * scale);

  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
  return canvas;
}
